import React from 'react';

import { Input, useTranslation } from '@testsigma/atoms';

interface OAuthFieldsProps {
  /** The watch function from react-hook-form used to retrieve the latest value from the form state */
  watch: (field: string) => any;
  /** Function to update the form field identified by fieldId with the value fieldValue*/
  handleUpdateFormValue: (fieldId: string, fieldValue: any) => void;
}

/**
 * Renders the UI for the fields (callback URL, access token, and refresh token) related to the OAuth login method.
 * @param props.watch The watch function from react-hook-form used to retrieve the latest value from the form state
 * @param props.handleUpdateFormValue Function to update the form field identified by fieldId with the value fieldValue
 *   - **Parameters:**
 *     - `fieldId` (string): The identifier of the form field to be updated.
 *     - `fieldValue` (any): The new value to set for the specified form field.
 *
 *   - **Returns:**
 *     - `void`: This function does not return a value; it updates the form field with the new value.
 *
 * @returns UI for fields associated with the OAuth login method.
 */
const OAuthFields: React.FC<OAuthFieldsProps> = (props: OAuthFieldsProps) => {
  const { handleUpdateFormValue, watch } = props;

  const { t } = useTranslation();

  return (
    <>
      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('callback-url.label')}
        </div>
        <Input
          isRequired={true}
          className='w-[320px] h-7'
          type='text'
          onChange={(value) => handleUpdateFormValue?.('callbackUrl', value)}
          hideErrorMessage={true}
          value={watch('callbackUrl')}
          placeholder = {t('callback-url.label')}
        />
      </div>

      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('access-token.label')}
        </div>
        <Input
          isDisabled={true}
          isRequired={true}
          className='w-[320px] h-7'
          type='password'
          onChange={(value) => handleUpdateFormValue?.('accessToken', value)}
          hideErrorMessage={true}
          value={watch('accessToken')}
          placeholder = {t('access-token.label')}
        />
      </div>

      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('refresh-token.label')}
        </div>
        <Input
          isDisabled={true}
          isRequired={true}
          type='password'
          className='w-[320px] h-7'
          onChange={(value) => handleUpdateFormValue?.('refreshToken', value)}
          hideErrorMessage={true}
          value={watch('refreshToken')}
          placeholder ={t('refresh-token.label')}
        />
      </div>
    </>
  );
};

export { OAuthFields };
